"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useModels } from "@/hooks/api/useBots";

const botSchema = z.object({
  name: z.string().min(2, {
    message: "Bot name must be at least 2 characters.",
  }),
  description: z.string().optional(),
  language: z.string(),
  api_mode: z.enum(["MANAGED", "BYOK"]).optional(),
  llm_model: z.string().min(1, "Please select an AI Model"),
});

export type BotFormValues = z.infer<typeof botSchema>;

interface BotFormProps {
  initialData?: Partial<BotFormValues>;
  onSubmit: (data: BotFormValues) => void;
  isLoading?: boolean;
}

export function BotForm({ initialData, onSubmit, isLoading }: BotFormProps) {
  const { data: models = [], isLoading: isModelsLoading } = useModels();

  const form = useForm<BotFormValues>({
    resolver: zodResolver(botSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      language: initialData?.language || "en",
      api_mode: initialData?.api_mode || "MANAGED",
      llm_model: initialData?.llm_model || "nvidia-llama-3.3",
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="E.g., Customer Support Bot" {...field} />
              </FormControl>
              <FormDescription>
                This is your bot&apos;s display name.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="What does this bot do?"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="api_mode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>API Mode</FormLabel>
              <FormControl>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  {...field}
                >
                  <option value="MANAGED">AnyBot Managed API</option>
                  <option value="BYOK">Bring Your Own Key (BYOK)</option>
                </select>
              </FormControl>
              <FormDescription>
                Choose how API costs are handled.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="llm_model"
          render={({ field }) => (
            <FormItem>
              <FormLabel>AI Model</FormLabel>
              <FormControl>
                <div className="grid gap-3">
                  {isModelsLoading ? (
                    <div className="text-sm text-muted-foreground">Loading AI Models...</div>
                  ) : (
                    models.filter(m => m.enabled).map((model) => (
                      <label
                        key={model.id}
                        className={`
                          relative flex cursor-pointer rounded-lg border bg-background p-4 shadow-sm focus:outline-none transition-colors
                          ${field.value === model.id ? 'border-primary ring-1 ring-primary' : 'border-input hover:bg-accent hover:text-accent-foreground'}
                        `}
                      >
                        <input
                          type="radio"
                          name="llm_model"
                          value={model.id}
                          className="sr-only"
                          onChange={field.onChange}
                          checked={field.value === model.id}
                        />
                        <span className="flex flex-1">
                          <span className="flex flex-col">
                            <span className="block text-sm font-medium text-foreground flex items-center gap-2">
                              {model.display_name}
                              {model.badge && (
                                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary ring-1 ring-inset ring-primary/20">
                                  {model.badge}
                                </span>
                              )}
                            </span>
                            <span className="mt-1 flex items-center text-sm text-muted-foreground">
                              {model.description}
                            </span>
                          </span>
                        </span>
                        {field.value === model.id && (
                          <svg
                            className="h-5 w-5 text-primary"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isLoading || isModelsLoading} className="w-full">
          {isLoading ? "Saving..." : "Save Bot"}
        </Button>
      </form>
    </Form>
  );
}
